import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Instagram, Facebook } from "lucide-react";
import hausLogo from "@/assets/haus-logo.png";
import euLogoEn from "@/assets/eu-logo-en.png";
import euLogoLv from "@/assets/eu-logo-lv.png";


export const Footer = () => {
  const { t, getLocalizedPath, language } = useLanguage();

  // Use LV logo for Latvian, EN logo for English and Russian
  const euLogo = language === 'lv' ? euLogoLv : euLogoEn;

  return (
    <footer id="contact" className="relative section-dark py-24 lg:py-32">
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          {/* Brand */}
          <div>
            <Link to={getLocalizedPath("/")} className="inline-block mb-6">
              <img 
                src={hausLogo} 
                alt="HAUS" 
                width={112}
                height={56}
                loading="lazy"
                decoding="async"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-cream/60 max-w-md leading-relaxed font-body text-sm">
              {t.hero.subtitle}
            </p>
            
            {/* Social links */}
            <div className="flex items-center gap-4 mt-6">
              <a 
                href="https://www.instagram.com/royalbathroomsriga/" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
                className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center text-cream/60 hover:text-gold hover:border-gold transition-all duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/RoyalBathrooms/" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center text-cream/60 hover:text-gold hover:border-gold transition-all duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="font-display text-lg font-semibold mb-6 text-cream">{t.footer.contact}</p>
            <ul className="space-y-4 text-cream/60 font-body">
              <li>
                <a href="mailto:haus@royal-bathrooms.com" className="hover:text-gold transition-colors">haus@royal-bathrooms.com</a>
              </li>
              <li>
                <a href="tel:+37167376780" className="hover:text-gold transition-colors">+371 6737 6780</a>
              </li>
              <li>Rīga, Cēsu iela 31 k-3, LV-1012</li>
            </ul>
            
            {/* Company Details */}
            {/* Working Hours */}
            <div className="mt-6 pt-6 border-t border-cream/10">
              <p className="text-cream/80 font-medium mb-2">{t.footer.workingHours}</p>
              <ul className="space-y-1 text-cream/50 text-sm">
                <li>{t.footer.workingHoursWeekdays}</li>
                <li>{t.footer.workingHoursSaturday}</li>
                <li>{t.footer.workingHoursSunday}</li>
              </ul>
            </div>
            
            {/* Company Details */}
            <div className="mt-4 pt-4 border-t border-cream/10">
              <p className="text-cream/80 font-medium mb-2">SIA "Royal Bathrooms"</p>
              <ul className="space-y-1 text-cream/50 text-sm">
                <li>{t.footer.regNr}: 40103384776</li>
                <li>{t.footer.vatNr}: LV40103384776</li>
              </ul>
            </div>
            
          </div>

          {/* Google Maps - square, dark, interactive with zoom */}
          <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-cream/10">
            <iframe
              src="https://maps.google.com/maps?q=56.966868994336004,24.141348167801276&t=&z=16&ie=UTF8&iwloc=B&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(120%) brightness(60%) sepia(15%) hue-rotate(180deg)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="HAUS store location"
              className="absolute inset-0"
            />
          </div>

          {/* EU Support */}
          <div>
            <div className="flex flex-col gap-4 max-w-xs">
              <img 
                src={euLogo} 
                alt="EU Recovery Fund" 
                className="h-auto w-full max-w-[220px] object-contain"
                loading="lazy"
                decoding="async"
              />
              <div className="text-cream/50 text-xs leading-relaxed font-body">
                <p className="mb-2">{t.footer.euSupportText}</p>
                <p className="mb-1">{t.footer.euSupportActivity}</p>
                <p>{t.footer.euSupportSolution}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-cream/10 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-cream/40 font-body">
            © {new Date().getFullYear()} HAUS. {t.footer.allRightsReserved}.
          </p>
          <div className="flex items-center gap-4 text-sm text-cream/40 font-body">
            <Link to={getLocalizedPath("/about")} className="hover:text-gold transition-colors">
              {language === "lv" ? "Par mums" : language === "ru" ? "О нас" : "About us"}
            </Link>
            <Link to={getLocalizedPath("/blog")} className="hover:text-gold transition-colors">
              {t.footer.blog || "Blog"}
            </Link>
            <Link to={getLocalizedPath("/privacy-policy")} className="hover:text-gold transition-colors">
              {t.footer.privacy}
            </Link>
            <Link to={getLocalizedPath("/cookies-policy")} className="hover:text-gold transition-colors">
              {t.footer.cookies}
            </Link>
            <Link to={getLocalizedPath("/purchase-terms")} className="hover:text-gold transition-colors">
              {t.footer.purchaseTerms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
